/**
 * Access Key Modal - User-friendly access key entry
 *
 * Simple, compassionate UI for entering access keys.
 * Designed for users in heightened states of urgency.
 */

import React, { useState, useEffect } from 'react';
import accessKeyService from '../services/accessKeyService';
import './AccessKeyModal.css';

const AccessKeyModal = ({ isOpen, onValidKey, onCancel }) => {
  const [accessKey, setAccessKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for stored key on mount
  useEffect(() => {
    if (isOpen) {
      const storedKey = accessKeyService.getStoredKey();
      if (storedKey) {
        setAccessKey(storedKey);
      }
    }
  }, [isOpen]);

  // Auto-format key as user types
  const handleKeyInput = (e) => {
    const input = e.target.value;
    const formatted = accessKeyService.formatKey(input);
    setAccessKey(formatted);
    setError('');
    setSuccessMessage('');
  };

  // Validate key
  const handleValidate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Basic validation
    if (!accessKey || accessKey.trim().length === 0) {
      setError('Please enter your access key');
      return;
    }

    // Check format
    if (!accessKeyService.isValidFormat(accessKey)) {
      setError('Invalid key format. Key should be 12 characters (e.g., STOP-A3B7-C9D2)');
      return;
    }

    setIsValidating(true);

    try {
      const result = await accessKeyService.validateKey(accessKey);

      if (result.valid) {
        setSuccessMessage(result.message || 'Access key validated successfully!');

        // Wait a moment to show success message
        setTimeout(() => {
          onValidKey(accessKey);
        }, 800);
      } else {
        setError(result.message || 'Invalid access key. Please try again.');
      }
    } catch (error) {
      setError('Unable to validate access key. Please try again.');
      console.error('[AccessKeyModal] Validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setAccessKey('');
    setError('');
    setSuccessMessage('');
    if (onCancel) {
      onCancel();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="access-key-modal-overlay" onClick={handleCancel}>
      <div className="access-key-modal" onClick={(e) => e.stopPropagation()}>
        <div className="access-key-modal-header">
          <h2>Access Key Required</h2>
          <button
            className="close-button"
            onClick={handleCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="access-key-modal-body">
          <p className="access-key-description">
            To upload files, please enter your access key. If you don't have an access key, contact your case worker or support services.
          </p>

          <form onSubmit={handleValidate}>
            <div className="form-group">
              <label htmlFor="accessKey">Access Key</label>
              <input
                type="text"
                id="accessKey"
                className={`access-key-input ${error ? 'error' : ''} ${successMessage ? 'success' : ''}`}
                value={accessKey}
                onChange={handleKeyInput}
                placeholder="STOP-XXXX-XXXX"
                maxLength={14}
                autoFocus
                disabled={isValidating || !!successMessage}
              />
              <small className="key-format-hint">
                Format: STOP-XXXX-XXXX (12 characters)
              </small>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="success-message">
                <span className="success-icon">✓</span>
                {successMessage}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancel}
                disabled={isValidating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isValidating || !accessKey || !!successMessage}
              >
                {isValidating ? 'Validating...' : 'Continue'}
              </button>
            </div>
          </form>

          <div className="access-key-help">
            <details>
              <summary>Need help?</summary>
              <div className="help-content">
                <p><strong>Where do I get an access key?</strong></p>
                <p>Access keys are provided by:</p>
                <ul>
                  <li>Victim services organizations</li>
                  <li>Legal aid providers</li>
                  <li>Case workers and advocates</li>
                  <li>Support hotlines</li>
                </ul>
                <p><strong>Having trouble?</strong></p>
                <p>If your access key isn't working:</p>
                <ul>
                  <li>Check that you typed it correctly</li>
                  <li>Make sure it hasn't expired</li>
                  <li>Contact the person who gave you the key</li>
                </ul>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessKeyModal;
